/**
 * @voltxampere/web — Polish effects store (Sprint 57)
 *
 * VISION §6.E P1 karakter polish layer — 3 effect MVP (this
 * sprint's pivot 1 scope):
 *
 *   1. wire-vibration  — wires carrying current > threshold get a
 *      visual sinüsoid perturbation (overlay path offset)
 *   2. snap-glow       — radial glow at the active snap target
 *      while a drag tool is hovering near a snap point
 *   3. hover-tooltip   — small HTML pill near the hovered component
 *
 * `prefersReducedMotion` (Sprint 29-5 store/fx-state.ts) gates the
 * vibration phase update so users with the OS preference set see
 * a still (un-perturbed) wire even when current is high.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, signal, type ReadonlySignal } from "@preact/signals-core";
import type { Point } from "@voltxampere/schema";
import { solveWireCurrents, type BranchMeasurement } from "@voltxampere/fx";

import { currentCircuit } from "./circuit.js";
import { currentDCResult } from "./dc-result.js";

/** Minimum |I| in amperes for a wire to qualify as "vibrating". */
export const VIBRATION_CURRENT_THRESHOLD_A = 0.1;

/** Saturating current — vibration amplitude clamps at this point. */
export const VIBRATION_CURRENT_SATURATION_A = 5.0;

/** Peak amplitude in CSS pixels at saturation (no zoom factor applied). */
export const VIBRATION_AMPLITUDE_PX = 1.6;

export interface WireVibrationState {
  readonly wireId: string;
  /** Normalised amplitude in [0, 1]; multiply by VIBRATION_AMPLITUDE_PX. */
  readonly amplitude: number;
}

/** Convert the web DCResultLike branchCurrents record into the fx
 *  BranchMeasurement[] shape the solver expects. */
function branchMeasurementsFromDC(
  dc: ReturnType<typeof currentDCResult.peek>,
): readonly BranchMeasurement[] {
  if (dc === null) return [];
  return Object.entries(dc.branchCurrents).map(([id, current]) => ({ id, current }));
}

/**
 * Per-wire vibration state derived from the DC result. Wires whose
 * |I| falls below the threshold are absent from the map.
 */
export const wireVibrationStates: ReadonlySignal<readonly WireVibrationState[]> = computed(() => {
  const circuit = currentCircuit.value;
  const dc = currentDCResult.value;
  if (circuit === null || dc === null) return [];
  const measurements = branchMeasurementsFromDC(dc);
  if (measurements.length === 0) return [];
  const result = solveWireCurrents(circuit, measurements);
  const out: WireVibrationState[] = [];
  for (const [wireId, current] of result.currents) {
    const mag = Math.abs(current);
    if (mag < VIBRATION_CURRENT_THRESHOLD_A) continue;
    const range = VIBRATION_CURRENT_SATURATION_A - VIBRATION_CURRENT_THRESHOLD_A;
    const t = Math.min(1, (mag - VIBRATION_CURRENT_THRESHOLD_A) / range);
    out.push({ wireId, amplitude: t });
  }
  return out;
});

/**
 * Snap-glow target: the canvas tool sets this when a drag is
 * hovering near a candidate snap point. `null` means no glow.
 */
export interface SnapGlowTarget {
  /** World-space coordinate of the snap point. */
  readonly point: Point;
  /** Glow intensity in [0, 1] — higher = closer to snap. */
  readonly intensity: number;
  /** Optional label key the overlay can render under the glow. */
  readonly labelKey?: string;
}

export const snapGlowTarget = signal<SnapGlowTarget | null>(null);

export function setSnapGlowTarget(target: SnapGlowTarget | null): void {
  snapGlowTarget.value = target;
}

/**
 * Hover tooltip — populated by the canvas hover handler.
 * componentId is the canonical id; the overlay resolves the
 * tooltip body from the circuit + i18n keys.
 */
export interface HoverTooltipTarget {
  readonly componentId: string;
  /** Anchor in client (screen) coordinates so the overlay can
   *  position the HTML element directly. */
  readonly clientX: number;
  readonly clientY: number;
}

export const hoverTooltipTarget = signal<HoverTooltipTarget | null>(null);

export function setHoverTooltipTarget(target: HoverTooltipTarget | null): void {
  hoverTooltipTarget.value = target;
}

/** Test helper — full reset. */
export function resetPolishEffectsForTests(): void {
  snapGlowTarget.value = null;
  hoverTooltipTarget.value = null;
}
